"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigationType, useLocation, useParams } from "react-router-dom";

import FunnelProgressIndicator from "@/components/FunnelProgressIndicator";
import AgentIdDisplay from "@/components/AgentIdDisplay";
import FunnelStepRenderer from "@/components/FunnelStepRenderer";
import PlanSummary from "@/components/price-customization/PlanSummary";

import { useFunnelState, ProductVariant } from "@/hooks/useFunnelState";
import { usePremiumCalculation } from "@/hooks/usePremiumCalculation";
import { useOfferLoader } from "@/hooks/useOfferLoader";
import { useFunnelActions } from "@/hooks/useFunnelActions";
import { useFunnelNavigation } from "@/hooks/useFunnelNavigation";
import { useFunnelRoot } from "@/shadow/ShadowRootContext";
import { BaseCoverageId } from "@/config/coverages";
import { useAgentId } from "@/context/AgentIdContext";
import { useFunnelNavigate } from "@/hooks/useFunnelNavigate";
import { normalizeAgentId } from "@/utils/agentId";
import { hashQueryParams, readUrlParam } from "@/utils/urlParams";
import { readCampaignCodeFromUrl } from "@/components/contact-details/CampaignCodeField";

const FunnelContainer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const funnelRoot = useFunnelRoot();
  const navigationType = useNavigationType();
  const navigate = useFunnelNavigate();
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language;

  const {
    funnelState,
    setBundlePrices,
    setBundleRecommendations,
    setSelectedRecommendation,
    setChipNumber,
    setSelectedProduct,
    setFinalPrice,
    setConfirmedOffer,
    setContractId,
    setContractToken,
    setCoverageSelections,
    setSubCoverageSelections,
    setSubCoverageSumInsured,
    setCoverageDeductibles,
    setCoverageDeductiblesOtherBand,
    setCoverageSumInsured,
    setCoverageSumInsuredPerCase,
    setEmergencyCareSumInsuredPerDay,
    setAnnualDeductible,
    setDailyTermination,
    setPaymentInterval,
    setPaymentMethod,
    setStartOfInsurance,
    setFirstDue,
    setIsOfferLoadingFromUrl,
    handlePetSelection: handlePetSelectionState,
    handlePetDetailsSubmit: handlePetDetailsSubmitState,
    applyRecommendation,
    discardCoverageCustomizations,
    discardProductSelection,
    changeCoverageSelectionsOnComparison,
    setHasSeenProductCustomization,
    handlePriceCustomizationSubmit: handlePriceCustomizationSubmitState,
    handleContactDetailsSubmit: handleContactDetailsSubmitState,
    setFunnelStateFromOffer,
    handleResetFunnel,
    setDispatchContract,
    setDispatchInvoice,
    setDispatchReminder,
    setDispatchEmail,
    setAgentDispatchEmail,
    setCampaignCode,
    applicationAnswers,
    setApplicationAnswers,
    setPremiumCorrections,
    setOfferLoadedFromUrl,
    setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocument,
    clearConsultationProtocolDocument,
    setConsultationProtocolPending,
    clearConsultationProtocolPending,
  } = useFunnelState();
  const { setDirectAgentId } = useAgentId();

  const {
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    preexistingConditionCategories,
    preexistingConditionComment,
    bundlePrices,
    bundleRecommendations,
    selectedProduct,
    finalPrice,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    street,
    housenumber,
    city,
    zipCode,
    country,
    contactBirthdate,
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageDeductiblesOtherBand,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
    dailyTermination,
    hasSeenProductCustomization,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    firstDue,
    isOfferLoadingFromUrl,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    campaignCode,
    customerGender,
    whitelabelPartner,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
    consultationProtocolDocumentId,
    consultationProtocolPendingFile,
    consultationProtocolPendingFilename,
  } = funnelState;
  const { hasSelectedPlan } = funnelState;

  const getLocalizedPath = React.useCallback((pathKey: string) => {
    const baseRoute = i18n.getResource(currentLocale, 'translation', pathKey) as string;
    return `/${currentLocale}${baseRoute.startsWith('/') ? baseRoute : `/${baseRoute}`}`;
  }, [currentLocale, i18n]);

  React.useEffect(() => {
    const homePath = getLocalizedPath('routes.home');
    const petDetailsPath = getLocalizedPath('routes.petDetails');

    if (location.pathname === homePath && !location.state?.fromOfferLoad) {
      const agentFromUrl = normalizeAgentId(readUrlParam("agent"));
      const campaignCodeFromUrl = readCampaignCodeFromUrl(new URLSearchParams(window.location.search), hashQueryParams());
      handleResetFunnel();
      if (agentFromUrl) {
        setDirectAgentId(agentFromUrl);
      }
      if (campaignCodeFromUrl) {
        setCampaignCode(campaignCodeFromUrl);
      }
      navigate(petDetailsPath, { replace: true, state: { fromReset: true } });
    }
  }, [location.pathname, getLocalizedPath, handleResetFunnel, navigate, setDirectAgentId, setCampaignCode]);

  const {
    goToHome,
    goToPetDetails,
    goToProductVariantSelection,
    goToProductCustomizationOptions,
    goToContactDetails,
    goToConfirmation,
  } = useFunnelNavigation({ funnelState, getLocalizedPath, handleResetFunnel, discardCoverageCustomizations });

  const {
    isLoading: isLoadingPrice,
    fetchBundleRecommendations,
    fetchCoverageRecommendation,
    currentApiPrice,
    currentOffer,
    coveragePremiums,
    declinedSubs,
    coverageUnderwritingReasons,
    declinedCoverages,
    underwritingFailed,
    underwritingReason,
  } = usePremiumCalculation({
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    selectedProduct,
    hasSelectedPlan,
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageDeductiblesOtherBand,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
    dailyTermination,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    contractId: funnelState.contractId,
    contractToken: funnelState.contractToken,
    agentId: funnelState.agentId,
    whitelabelPartner: funnelState.whitelabelPartner,
    premiumCorrections: funnelState.premiumCorrections,
    currentLocale,
  });

  useOfferLoader({
    setFunnelStateFromOffer,
    handleResetFunnel,
    setIsOfferLoadingFromUrl,
    currentLocale,
  });

  const previousPathnameRef = React.useRef(location.pathname);
  React.useEffect(() => {
    const confirmationPath = getLocalizedPath('routes.confirmation');
    // Only count it as "leaving" confirmation when we were actually there before —
    // right after silently creating the offer on contact-details submit, this effect
    // can fire once with the not-yet-committed pathname (still /adressdaten), which
    // must not be mistaken for having left the confirmation page.
    if (offerLoadedFromUrl && previousPathnameRef.current === confirmationPath && location.pathname !== confirmationPath) {
      setHasLeftConfirmationAfterOfferLoad(true);
    }
    previousPathnameRef.current = location.pathname;
  }, [offerLoadedFromUrl, location.pathname, getLocalizedPath, setHasLeftConfirmationAfterOfferLoad]);

  // Tracks whether the customer has ever actually landed on the coverage customization page —
  // regardless of how they got there — so "Zurück" from contact details can skip straight to
  // the product variant table when they took the "Auswählen" fast path and never saw it.
  React.useEffect(() => {
    if (hasSeenProductCustomization) return;
    if (location.pathname === getLocalizedPath('routes.productCustomization')) {
      setHasSeenProductCustomization(true);
    }
  }, [location.pathname, getLocalizedPath, hasSeenProductCustomization, setHasSeenProductCustomization]);

  // `bundleRecommendations` is normally populated once, from the pet-details submit handler
  // (handlePetDetailsSubmitAndFetch) — but a customer who arrives via an offer link
  // (?contractID=...&contractToken=...) never submits that form, so it stays null forever. If
  // they then navigate to the product variant table, every tier's price permanently shows the
  // loading spinner (ProductVariantTable's PriceDisplay treats `price === null` as "still
  // loading", not "nothing to show") since nothing ever fetches it. Fetch it here instead,
  // whenever that page is reached with pet data available but no recommendations yet.
  React.useEffect(() => {
    if (bundleRecommendations) return;
    if (location.pathname !== getLocalizedPath('routes.productVariantSelection')) return;
    if (!petType || !petName || !breed || !birthDate || !selectedBreedType || !gender) return;

    let isActive = true;
    (async () => {
      const recommendations = await fetchBundleRecommendations(
        petType,
        petName,
        breed,
        birthDate,
        selectedBreedType,
        gender,
        isCastrated,
        isHouseCat,
        selectedDogSize,
        coverageSelections,
        currentLocale,
        funnelState.agentId,
        startOfInsurance,
        () => isActive
      );
      if (!isActive || !recommendations) return;
      setBundleRecommendations(recommendations);
      setBundlePrices({
        S: recommendations.S.grossPremiumProRata ?? 0,
        M: recommendations.M.grossPremiumProRata ?? 0,
        L: recommendations.L.grossPremiumProRata ?? 0,
      });
    })();
    return () => {
      isActive = false;
    };
  }, [
    bundleRecommendations, location.pathname, getLocalizedPath, petType, petName, breed, birthDate,
    selectedBreedType, gender, isCastrated, isHouseCat, selectedDogSize, coverageSelections,
    currentLocale, funnelState.agentId, startOfInsurance, fetchBundleRecommendations, setBundleRecommendations, setBundlePrices,
  ]);

  const {
    handlePetDetailsSubmitAndFetch,
    handleSelectProductVariant,
    handleProductVariantSelectionAndNavigate: handleProductVariantSelectionAndNavigateAction,
    handleCreateApplication,
    handleContactDetailsSubmitAndCreateOffer,
    isSubmittingOffer,
  } = useFunnelActions({
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    preexistingConditionCategories,
    preexistingConditionComment,
    selectedProduct,
    hasSelectedPlan,
    bundleRecommendations,
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageDeductiblesOtherBand,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
    dailyTermination,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    firstDue,
    contractId: funnelState.contractId,
    contractToken: funnelState.contractToken,
    agentId: funnelState.agentId,
    whitelabelPartner,
    premiumCorrections: funnelState.premiumCorrections,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    campaignCode,
    contactBirthdate,
    customerGender: funnelState.customerGender,
    contactFirstName: funnelState.contactFirstName, // Pass contactFirstName
    contactLastName: funnelState.contactLastName,   // Pass contactLastName
    handlePetDetailsSubmitState,
    setBundlePrices,
    setBundleRecommendations,
    applyRecommendation,
    setFinalPrice,
    setConfirmedOffer,
    setContractId,
    setContractToken,
    handleContactDetailsSubmitState,
    fetchBundleRecommendations,
    underwritingFailed,
    underwritingReason,
    currentLocale,
    handleResetFunnel,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
    setOfferLoadedFromUrl,
    setHasLeftConfirmationAfterOfferLoad,
    consultationProtocolDocumentId,
    consultationProtocolPendingFile,
    consultationProtocolPendingFilename,
    setConsultationProtocolDocument,
    clearConsultationProtocolDocument,
    clearConsultationProtocolPending,
  });

  const handlePetSelectionAndNavigate = (selectedPetType: "cat" | "dog") => {
    handlePetSelectionState(selectedPetType);
    goToPetDetails();
  };

  const handlePetDetailsSubmitAndNavigate = async (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedCoverageSelections: Partial<Record<BaseCoverageId, boolean>>,
    submittedStartOfInsurance: Date,
    submittedChipNumber: string | null
  ) => {
    await handlePetDetailsSubmitAndFetch(
      submittedPetName,
      selectedBreed,
      submittedBreedLabel,
      submittedBirthDate,
      submittedSelectedBreedType,
      submittedGender,
      submittedIsCastrated,
      submittedIsHouseCat,
      submittedSelectedDogSize,
      submittedCoverageSelections,
      submittedStartOfInsurance,
      submittedChipNumber
    );
  };

  const handleProductVariantSelectionAndNavigate = (product: ProductVariant) => {
    handleProductVariantSelectionAndNavigateAction(product);
  };

  /** "Auswählen" on the product variant table: accept this tier's recommendation as-is and
   * skip straight to contact details, bypassing the coverage customization page. */
  const handleSelectProductVariantAndSkipToContact = (product: ProductVariant) => {
    handleSelectProductVariant(product);
    goToContactDetails();
  };

  /** "Anpassungen verwerfen" on the coverage customization page: drop the chosen package and all
   * edits made on top of it, then go back to the product variant table to pick a package afresh. */
  const handleBackFromProductCustomization = () => {
    discardProductSelection();
    // Navigate only: `goToProductVariantSelection` would re-apply the (stale) selected tier's recommendation and
    // undo the reset above (selected recommendation, price and "already applied" flag).
    navigate(getLocalizedPath('routes.productVariantSelection'));
  };

  /** "Zurück" on the contact details page: skip the coverage customization page entirely and
   * go straight to the product variant table if the customer never actually saw customization
   * (i.e. they took the "Auswählen" fast path) — otherwise go back to customization as usual. */
  const handleBackFromContactDetails = () => {
    // An offer the customer jumped in with has no bundle to pick, so skip the variant table too.
    if (hasSeenProductCustomization || !selectedProduct) {
      goToProductCustomizationOptions();
    } else {
      goToProductVariantSelection();
    }
  };

  const handlePriceCustomizationSubmitAndNavigate = (
    product: ProductVariant | null,
    price: number,
    submittedCoverageDeductibles: Partial<Record<BaseCoverageId, number>>,
    submittedSubCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>,
    submittedCoverageSumInsured: Partial<Record<BaseCoverageId, number>>,
    submittedDailyTermination: boolean,
    interval: 1 | 2 | 4 | 12,
    method: "credit_card" | "ESR",
    startDate: Date
  ) => {
    handlePriceCustomizationSubmitState(
      product,
      price,
      submittedCoverageDeductibles,
      submittedSubCoverageSelections,
      submittedCoverageSumInsured,
      submittedDailyTermination,
      interval,
      method,
      startDate
    );
    goToContactDetails();
  };

  const handleContactDetailsContinue = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string,
    submittedBirthdate: Date | undefined,
    submittedGender: "male" | "female" | ""
  ) => {
    const offerCreated = await handleContactDetailsSubmitAndCreateOffer(
      firstName,
      lastName,
      email,
      phone,
      submittedStreet,
      submittedHousenumber,
      submittedCity,
      submittedZip,
      submittedCountry,
      submittedBirthdate,
      submittedGender
    );
    if (offerCreated) {
      goToConfirmation();
    }
  };

  React.useEffect(() => {
    // No redirect to a dedicated page anymore; errors are surfaced inline/toasts.
    if (underwritingFailed && underwritingReason) {
      console.warn("Underwriting failed:", underwritingReason);
    }
  }, [underwritingFailed, underwritingReason]);

  // The funnel lives in a shadow root inside someone else's page: give the host a landmark name and the funnel's own
  // language (the embedding page's `lang` may differ), so screen readers announce and pronounce it correctly.
  React.useEffect(() => {
    const host = funnelRoot instanceof ShadowRoot ? funnelRoot.host : null;
    if (!host) return;
    host.setAttribute("role", "region");
    host.setAttribute("aria-label", t("a11y.region"));
    host.setAttribute("lang", i18n.language);
  }, [funnelRoot, t, i18n.language]);

  // A step change is a page change for a single-page app: move focus to the new step's heading so keyboard and
  // screen-reader users land at its start (not on a button that no longer exists). Skipped on the first render.
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Redirects (e.g. from the start route right after loading) are not a step change the visitor made.
    if (navigationType === "REPLACE") return;
    const frame = requestAnimationFrame(() => {
      const heading = funnelRoot.querySelector<HTMLElement>("h1, h2, h3");
      if (!heading) return;
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [funnelRoot, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- reacts to path changes only

  const showPlanSummary =
    location.pathname === getLocalizedPath('routes.productCustomization') ||
    location.pathname === getLocalizedPath('routes.addressDetails');

  return (
    <React.Fragment>
      <AgentIdDisplay />
      <FunnelProgressIndicator getLocalizedPath={getLocalizedPath} />

      {showPlanSummary && hasSelectedPlan && (
        <div id="funnel-cart" className="w-full max-w-4xl mx-auto sticky top-(--sticky-top) z-20 mb-8">
          <PlanSummary
            currentOffer={currentOffer}
            petBirthDate={birthDate}
            currentFinalPrice={currentApiPrice}
            paymentInterval={paymentInterval}
            isLoading={isLoadingPrice}
            coverageSelections={coverageSelections}
            declinedCoverages={declinedCoverages}
            subCoverageSelections={subCoverageSelections}
            subCoverageSumInsured={subCoverageSumInsured}
            coverageDeductibles={coverageDeductibles}
            coverageSumInsured={coverageSumInsured}
            coverageSumInsuredPerCase={coverageSumInsuredPerCase}
            emergencyCareSumInsuredPerDay={emergencyCareSumInsuredPerDay}
            annualDeductible={annualDeductible}
            underwritingFailed={underwritingFailed}
            underwritingReason={underwritingReason}
          />
        </div>
      )}

      <FunnelStepRenderer
        funnelState={funnelState}
        getLocalizedPath={getLocalizedPath}
        isLoadingPrice={isLoadingPrice}
        fetchBundleRecommendations={fetchBundleRecommendations}
        currentApiPrice={currentApiPrice}
        currentOffer={currentOffer}
        coveragePremiums={coveragePremiums}
        declinedSubs={declinedSubs}
        coverageUnderwritingReasons={coverageUnderwritingReasons}
        declinedCoverages={declinedCoverages}
        fetchCoverageRecommendation={fetchCoverageRecommendation}
        underwritingFailed={underwritingFailed}
        underwritingReason={underwritingReason}
        handlePetDetailsSubmitAndNavigate={handlePetDetailsSubmitAndNavigate}
        handleSelectProductVariant={handleSelectProductVariant}
        handleSelectProductVariantAndSkipToContact={handleSelectProductVariantAndSkipToContact}
        handleProductVariantSelectionAndNavigate={handleProductVariantSelectionAndNavigate}
        handlePriceCustomizationSubmitAndNavigate={handlePriceCustomizationSubmitAndNavigate}
        handleCreateApplication={handleCreateApplication}
        handleContactDetailsContinue={handleContactDetailsContinue}
        handleContactDetailsFieldBlur={handleContactDetailsSubmitState}
        isSubmittingOffer={isSubmittingOffer}
        goToHome={goToHome}
        goToPetDetails={goToPetDetails}
        goToProductVariantSelection={goToProductVariantSelection}
        handleBackFromProductCustomization={handleBackFromProductCustomization}
        handleBackFromContactDetails={handleBackFromContactDetails}
        goToProductCustomizationOptions={goToProductCustomizationOptions}
        goToContactDetails={goToContactDetails}
        goToConfirmation={goToConfirmation}
        setSelectedProduct={setSelectedProduct}
        setChipNumber={setChipNumber}
        changeCoverageSelectionsOnComparison={changeCoverageSelectionsOnComparison}
        setCoverageSelections={setCoverageSelections}
        setSubCoverageSelections={setSubCoverageSelections}
        setSubCoverageSumInsured={setSubCoverageSumInsured}
        setCoverageDeductibles={setCoverageDeductibles}
        setCoverageDeductiblesOtherBand={setCoverageDeductiblesOtherBand}
        setCoverageSumInsured={setCoverageSumInsured}
        setCoverageSumInsuredPerCase={setCoverageSumInsuredPerCase}
        setEmergencyCareSumInsuredPerDay={setEmergencyCareSumInsuredPerDay}
        setAnnualDeductible={setAnnualDeductible}
        setDailyTermination={setDailyTermination}
        setBundleRecommendations={setBundleRecommendations}
        setPaymentInterval={setPaymentInterval}
        setPaymentMethod={setPaymentMethod}
        setStartOfInsurance={setStartOfInsurance}
        setFirstDue={setFirstDue}
        onPetTypeChange={handlePetSelectionState}
        setDispatchContract={setDispatchContract}
        setDispatchInvoice={setDispatchInvoice}
        setDispatchReminder={setDispatchReminder}
        setDispatchEmail={setDispatchEmail}
        setAgentDispatchEmail={setAgentDispatchEmail}
        setCampaignCode={setCampaignCode}
        applicationAnswers={applicationAnswers}
        setApplicationAnswers={setApplicationAnswers}
        setConsultationProtocolDocument={setConsultationProtocolDocument}
        clearConsultationProtocolDocument={clearConsultationProtocolDocument}
        setConsultationProtocolPending={setConsultationProtocolPending}
        clearConsultationProtocolPending={clearConsultationProtocolPending}
        setBundlePrices={setBundlePrices}
      />
    </React.Fragment>
  );
};

export default FunnelContainer;
